import { useCustomerContacts, useCustomers } from "@/hooks/Customer";
import { CustomerContact } from "@/services/api";
import { Persona, Combobox, Option, Spinner, Label } from "@fluentui/react-components";
import * as React from "react";
import { useInView } from "react-hook-inview";

export interface IContactPickerProps {
	selectedContactId?: string | null;
	onSelectedContactChange?: (id?: string | null) => void;
	placeholder?: string;
	emptyOptionLabel?: string;
}
const SHOWCOUNT = 30;
export function ContactPicker(props: IContactPickerProps) {
	const { data: contacts } = useCustomerContacts();

	const [ddIsOpen, setDDIsOpen] = React.useState(false);
	const [node, inView] = useInView();
	const [customSearch, setCustomSearch] = React.useState<string>("");
	const [selectedCustomer, setSelectedCustomer] = React.useState<
		CustomerContact | null | undefined
	>();
	const [showCount, setShowCount] = React.useState(SHOWCOUNT);

	const filteredContacts = React.useMemo(() => {
		if (!customSearch || !contacts?.length) return contacts ?? [];

		let visibleCustomers = contacts.filter(
			(i) => (i.name?.toLocaleLowerCase().indexOf(customSearch.toLocaleLowerCase()) ?? -1) > -1
		);
		return visibleCustomers;
	}, [contacts, customSearch]);

	const visibleContacts = React.useMemo(() => {
		const contactsOut = filteredContacts?.slice(0, showCount) ?? [];
		return contactsOut;
	}, [filteredContacts, showCount]);

	React.useEffect(() => {
		if (ddIsOpen && inView) {
			setShowCount(() => showCount + SHOWCOUNT);
		} else if (!ddIsOpen) {
			setShowCount(SHOWCOUNT);
		}
	}, [ddIsOpen, inView]);

	React.useEffect(() => {
		if (!!props.selectedContactId) {
			setSelectedCustomer(contacts?.find((i) => i.identityId === props.selectedContactId));
		}
	}, [contacts, props.selectedContactId]);

	return (
		<div>
			<Combobox
				value={!!customSearch ? customSearch : selectedCustomer?.name ?? ""}
				placeholder={props.placeholder}
				onChange={(ev) => {
					const value = ev.target.value;
					setCustomSearch(value);
				}}
				onOpenChange={(ev, ctx) => {
					setDDIsOpen(ctx.open);
					setCustomSearch("");
				}}
				defaultSelectedOptions={[]}
				onOptionSelect={(ev, opts) => {
					const selection = opts.selectedOptions[0];
					const match = contacts?.find((i) => i.identityId === selection);
					setSelectedCustomer(match);
					if (props.onSelectedContactChange) props.onSelectedContactChange(selection);
				}}
				selectedOptions={!!selectedCustomer?.identityId ? [selectedCustomer.identityId] : []}>
				{!!props.emptyOptionLabel && (
					<Option key={"empty-option"} value="" text={props.emptyOptionLabel}>
						{props.emptyOptionLabel}
					</Option>
				)}
				{visibleContacts.map((i) => (
					<Option key={i.identityId!} value={i.identityId!} text={i.name || ""}>
						<Persona
							size="large"
							avatar={{ color: "colorful" }}
							name={i?.name ?? ""}
							primaryText={i?.name}
							secondaryText={i?.jobTitle}></Persona>
					</Option>
				))}
				{filteredContacts.length !== visibleContacts.length && (
					<Option disabled value="loading..." text="Loading..." ref={node}>
						<Spinner label={"Loading..."} size="small" />
					</Option>
				)}
			</Combobox>
		</div>
	);
}
